/**
 * World Clock Bar
 *
 * Compact horizontal bar showing team timezones.
 * Fixed at the top of the dashboard page.
 */

import { useEffect, useState } from 'react';
import { Globe, Sun, Moon, MessageSquare, Mic, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useSidebar } from '@/components/layout/DashboardLayout';

interface TeamLocation {
  name: string;
  timezone: string;
  label: string;
  isHQ?: boolean;
}

const TEAM_LOCATIONS: TeamLocation[] = [
  { name: 'Minnesota', timezone: 'America/Chicago', label: 'HQ', isHQ: true },
  { name: 'Danny', timezone: 'America/New_York', label: 'EST' },
  { name: 'Salma', timezone: 'Europe/Paris', label: 'CET' },
  { name: 'Amar', timezone: 'Asia/Kolkata', label: 'IST' },
];

const BUSINESS_HOURS = { start: 8, end: 17 }; // 8am-5pm

function formatTime(date: Date, timezone: string): string {
  return date.toLocaleTimeString('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

function getHour(date: Date, timezone: string): number {
  const timeStr = date.toLocaleTimeString('en-US', {
    timeZone: timezone,
    hour: 'numeric',
    hour12: false,
  });
  return parseInt(timeStr, 10);
}

function isBusinessHours(date: Date, timezone: string): boolean {
  const hour = getHour(date, timezone);
  return hour >= BUSINESS_HOURS.start && hour < BUSINESS_HOURS.end;
}

function isDaytime(date: Date, timezone: string): boolean {
  const hour = getHour(date, timezone);
  return hour >= 6 && hour < 20;
}

interface ClockItemProps {
  location: TeamLocation;
  currentTime: Date;
}

function ClockItem({ location, currentTime }: ClockItemProps) {
  const time = formatTime(currentTime, location.timezone);
  const isDay = isDaytime(currentTime, location.timezone);
  const isWorkHours = location.isHQ && isBusinessHours(currentTime, location.timezone);

  return (
    <div
      className={cn(
        'flex items-center gap-2 px-3 py-1.5 rounded-md transition-all',
        location.isHQ && isWorkHours && 'bg-primary/10 ring-1 ring-primary/30'
      )}
    >
      {/* Day/Night indicator */}
      <div
        className={cn(
          'flex items-center justify-center w-5 h-5 rounded-full',
          isDay ? 'bg-amber-100 text-amber-600' : 'bg-slate-700 text-slate-300'
        )}
      >
        {isDay ? <Sun className="h-3 w-3" /> : <Moon className="h-3 w-3" />}
      </div>

      {/* Name and time */}
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-medium">{location.name}</span>
        {location.isHQ && (
          <span className="text-[9px] font-medium px-1 py-0.5 rounded bg-primary/10 text-primary">
            HQ
          </span>
        )}
      </div>

      {/* Time */}
      <span
        className={cn(
          'font-mono text-sm font-semibold tabular-nums',
          location.isHQ && isWorkHours && 'text-primary'
        )}
      >
        {time}
      </span>
    </div>
  );
}

interface WorldClockBarProps {
  onAskPoco?: () => void;
  onOreja?: () => void;
  orejaBadgeCount?: number;
  orejaIsProcessing?: boolean;
}

export function WorldClockBar({ onAskPoco, onOreja, orejaBadgeCount = 0, orejaIsProcessing = false }: WorldClockBarProps) {
  const [currentTime, setCurrentTime] = useState(new Date());
  const { isCollapsed } = useSidebar();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, []);

  return (
    <div className={cn(
      "fixed bottom-0 right-0 z-50 px-4 sm:px-6 lg:px-8 py-1.5 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-t",
      isCollapsed ? 'left-0 md:left-16' : 'left-0 md:left-52'
    )}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Globe className="h-3.5 w-3.5" />
          <span className="text-xs font-medium hidden sm:inline">Team</span>
        </div>

        <div className="flex items-center gap-2 sm:gap-4 overflow-x-auto scrollbar-hide">
          {TEAM_LOCATIONS.map((location) => (
            <ClockItem key={location.name} location={location} currentTime={currentTime} />
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="default"
            size="sm"
            className="h-7 text-xs bg-primary/90 hover:bg-primary shadow-sm gap-1"
            onClick={onOreja}
          >
            <Mic className="h-3.5 w-3.5" />
            Oreja
            {orejaIsProcessing && <Loader2 className="ml-0.5 h-3 w-3 animate-spin" />}
            {orejaBadgeCount > 0 && (
              <span className="ml-0.5 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                {orejaBadgeCount}
              </span>
            )}
          </Button>
          <Button
            variant="default"
            size="sm"
            className="h-7 text-xs bg-primary/90 hover:bg-primary shadow-sm gap-1"
            onClick={onAskPoco}
          >
            <MessageSquare className="h-3.5 w-3.5" />
            Ask Poco
          </Button>
        </div>
      </div>
    </div>
  );
}

export default WorldClockBar;
